import { clsx } from 'clsx';

import styles from './Toggle.module.scss';

/**
 * Props for `<Toggle>`.
 *
 * Required: `checked`, `onChange`. Always controlled — chartv2 owns no
 * internal state for the switch.
 */
export interface ToggleProps {
  /** Current checked state. */
  checked: boolean;
  /** Called with the next checked state when the user clicks the switch. */
  onChange: (next: boolean) => void;
  /** Disables clicks and renders a muted state. */
  disabled?: boolean;
  /** Accessible label, forwarded to the `<button>` element. */
  'aria-label'?: string;
  /** Extra class name forwarded to the button element. */
  className?: string;
  /** Test selector forwarded as `data-testid`. */
  'data-testid'?: string;
}

/**
 * Minimal on/off switch used by the per-series chart-type dropdown.
 * Mirrors `@toggle/design-system`'s `Toggle` visual (pill with animated
 * circle) without pulling in the full dependency.
 */
export const Toggle = ({
  checked,
  onChange,
  disabled,
  'aria-label': ariaLabel,
  className,
  'data-testid': testId,
}: ToggleProps) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={ariaLabel}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={clsx(
      styles.root,
      checked && styles.checked,
      disabled && styles.disabled,
      className
    )}
    data-testid={testId}
  >
    <span className={clsx(styles.circle, checked && styles.circleChecked)} />
  </button>
);
